import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronUp, MessageSquare, Flag, CheckCircle, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ShareButtons from '../components/ShareButtons';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(d) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 5) return "A l'instant";
  if (mins < 60) return 'il y a ' + mins + ' min';
  const h = Math.floor(mins / 60);
  if (h < 24) return 'il y a ' + h + 'h';
  return 'il y a ' + Math.floor(h / 24) + ' j';
}

function ReplyItem(props) {
  var reply = props.reply;
  var postAuthorId = props.postAuthorId;
  var postId = props.postId;
  var onSolutionMark = props.onSolutionMark;
  var onRefresh = props.onRefresh;
  var isNested = props.isNested;

  var auth = useAuth();
  var user = auth.user;
  var setShowReplyForm = useState(false);
  var showReplyForm = setShowReplyForm[0];
  var setShowReplyFormFn = setShowReplyForm[1];
  var replyState = useState('');
  var replyContent = replyState[0];
  var setReplyContent = replyState[1];
  var submittingState = useState(false);
  var submitting = submittingState[0];
  var setSubmitting = submittingState[1];
  var upvotedState = useState(reply.user_upvoted || false);
  var upvoted = upvotedState[0];
  var setUpvoted = upvotedState[1];
  var upvoteCountState = useState(reply.upvote_count || 0);
  var upvoteCount = upvoteCountState[0];
  var setUpvoteCount = upvoteCountState[1];

  function handleUpvote() {
    if (!user) { toast.error('Connectez-vous'); return; }
    axios.post(API + '/api/forum/replies/' + reply.reply_id + '/upvote', {}, { withCredentials: true })
      .then(function(r) {
        setUpvoted(r.data.upvoted);
        setUpvoteCount(function(prev) { return r.data.upvoted ? prev + 1 : prev - 1; });
      })
      .catch(function() {});
  }

  function handleNestedReply(e) {
    e.preventDefault();
    if (!user) { toast.error('Connectez-vous'); return; }
    setSubmitting(true);
    axios.post(API + '/api/forum/posts/' + postId + '/replies', { content: replyContent, parent_reply_id: reply.reply_id }, { withCredentials: true })
      .then(function() {
        setShowReplyFormFn(false);
        setReplyContent('');
        if (onRefresh) { onRefresh(); }
        toast.success('Reponse publiee');
      })
      .catch(function(err) {
        var msg = err && err.response && err.response.data && err.response.data.detail ? err.response.data.detail : 'Erreur';
        toast.error(msg);
      })
      .finally(function() { setSubmitting(false); });
  }

  function handleFlag() {
    axios.post(API + '/api/forum/replies/' + reply.reply_id + '/flag', {}, { withCredentials: true }).catch(function() {});
  }

  var nested = reply.nested || [];
  var bgClass = isNested ? 'ml-6 border-l-2 border-white/5 pl-3 ' : '';
  var solutionClass = reply.is_solution ? 'bg-[#06d6a0]/5 border border-[#06d6a0]/20 rounded-xl ' : '';

  return React.createElement('div', { className: bgClass + solutionClass + 'p-3 space-y-2' },
    reply.is_solution && React.createElement('div', { className: 'flex items-center gap-1.5 text-xs text-[#06d6a0] font-medium' },
      React.createElement(CheckCircle, { size: 12 }), ' Solution'
    ),
    React.createElement('div', { className: 'flex items-center gap-2' },
      React.createElement('div', { className: 'w-7 h-7 rounded-full bg-[#ff6b6b]/20 flex items-center justify-center text-[#ff6b6b] text-xs font-bold' },
        reply.author_name && reply.author_name[0] ? reply.author_name[0].toUpperCase() : '?'
      ),
      React.createElement('span', { className: 'text-white text-xs font-medium' }, reply.author_name),
      React.createElement('span', { className: 'text-[#a0a0ab] text-xs ml-auto' }, timeAgo(reply.created_at))
    ),
    React.createElement('p', { className: 'text-sm leading-relaxed text-[#e8e8e8]' }, reply.content),
    React.createElement('div', { className: 'flex items-center gap-3' },
      React.createElement('button', {
        onClick: handleUpvote,
        className: 'flex items-center gap-1 text-xs transition-colors ' + (upvoted ? 'text-[#06d6a0]' : 'text-[#a0a0ab] hover:text-white')
      }, React.createElement(ChevronUp, { size: 12 }), ' ' + upvoteCount),
      !isNested && user && React.createElement('button', {
        onClick: function() { setShowReplyFormFn(function(v) { return !v; }); },
        className: 'text-xs text-[#a0a0ab] hover:text-white transition-colors flex items-center gap-1'
      }, React.createElement(MessageSquare, { size: 11 }), ' Repondre'),
      !isNested && user && postAuthorId === user.id && !reply.is_solution && React.createElement('button', {
        onClick: function() { if (onSolutionMark) { onSolutionMark(reply.reply_id); } },
        className: 'text-xs text-[#06d6a0] hover:text-white flex items-center gap-1 transition-colors'
      }, React.createElement(CheckCircle, { size: 11 }), ' Solution'),
      React.createElement('button', {
        onClick: handleFlag,
        className: 'text-xs text-[#a0a0ab] hover:text-[#ff6b6b] transition-colors ml-auto'
      }, React.createElement(Flag, { size: 11 }))
    ),
    showReplyForm && React.createElement('form', { onSubmit: handleNestedReply, className: 'space-y-2' },
      React.createElement('textarea', {
        value: replyContent,
        onChange: function(e) { setReplyContent(e.target.value); },
        placeholder: 'Votre reponse...',
        rows: 2,
        className: 'w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2 text-xs resize-none focus:outline-none'
      }),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement('button', { type: 'button', onClick: function() { setShowReplyFormFn(false); }, className: 'text-xs text-[#a0a0ab] hover:text-white px-3 py-1.5' }, 'Annuler'),
        React.createElement('button', { type: 'submit', disabled: submitting, className: 'btn-primary text-xs py-1.5 px-3' }, submitting ? '...' : 'Repondre')
      )
    ),
    nested.map(function(n) {
      return React.createElement(ReplyItem, {
        key: n.reply_id,
        reply: n,
        postAuthorId: postAuthorId,
        postId: postId,
        isNested: true
      });
    })
  );
}

export default function ForumPost() {
  var params = useParams();
  var postId = params.postId;
  var navigate = useNavigate();
  var auth = useAuth();
  var user = auth.user;
  var postState = useState(null);
  var post = postState[0];
  var setPost = postState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var replyState = useState('');
  var replyContent = replyState[0];
  var setReplyContent = replyState[1];
  var submittingState = useState(false);
  var submitting = submittingState[0];
  var setSubmitting = submittingState[1];
  var upvotedState = useState(false);
  var upvoted = upvotedState[0];
  var setUpvoted = upvotedState[1];

  var fetchPost = useCallback(function() {
    axios.get(API + '/api/forum/posts/' + postId, { withCredentials: true })
      .then(function(r) {
        setPost(r.data);
        setUpvoted(r.data.user_upvoted);
      })
      .catch(function() { navigate('/communaute'); })
      .finally(function() { setLoading(false); });
  }, [postId, navigate]);

  useEffect(function() { fetchPost(); }, [fetchPost]);

  function handleUpvotePost() {
    if (!user) { toast.error('Connectez-vous'); return; }
    axios.post(API + '/api/forum/posts/' + postId + '/upvote', {}, { withCredentials: true })
      .then(function(r) {
        setUpvoted(r.data.upvoted);
        setPost(function(p) {
          return Object.assign({}, p, { upvote_count: r.data.upvoted ? p.upvote_count + 1 : p.upvote_count - 1 });
        });
      })
      .catch(function() {});
  }

  function handleSubmitReply(e) {
    e.preventDefault();
    if (!user) { toast.error('Connectez-vous'); return; }
    setSubmitting(true);
    axios.post(API + '/api/forum/posts/' + postId + '/replies', { content: replyContent }, { withCredentials: true })
      .then(function() {
        toast.success('Reponse publiee ! +5 XP');
        setReplyContent('');
        fetchPost();
      })
      .catch(function(err) {
        var msg = err && err.response && err.response.data && err.response.data.detail ? err.response.data.detail : 'Erreur';
        toast.error(msg);
      })
      .finally(function() { setSubmitting(false); });
  }

  function handleSolutionMark(replyId) {
    axios.post(API + '/api/forum/replies/' + replyId + '/solution', {}, { withCredentials: true })
      .then(function() {
        fetchPost();
        toast.success('Solution marquee !');
      })
      .catch(function() {});
  }

  function handleFlagPost() {
    axios.post(API + '/api/forum/posts/' + postId + '/flag', {}, { withCredentials: true }).catch(function() {});
  }

  if (loading) {
    return React.createElement(Layout, null,
      React.createElement('div', { className: 'flex items-center justify-center h-64' },
        React.createElement('div', { className: 'w-8 h-8 border-2 border-[#ff6b6b] border-t-transparent rounded-full animate-spin' })
      )
    );
  }

  if (!post) { return null; }

  var replies = post.replies || [];

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button onClick={function() { navigate('/communaute'); }}
          className="flex items-center gap-2 text-[#a0a0ab] hover:text-white transition-colors text-sm mb-4">
          <ArrowLeft size={16} /> Communaute
        </button>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{post.type_icon}</span>
            <span className="text-xs text-[#a0a0ab]">{post.category}</span>
            {post.is_closed && (
              <span className="flex items-center gap-1 text-xs text-[#a0a0ab] bg-white/10 px-1.5 py-0.5 rounded-full">
                <Lock size={10} /> Ferme
              </span>
            )}
          </div>
          <h1 className="text-xl font-heading font-bold text-white mb-3">{post.title}</h1>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-full bg-[#ff6b6b]/20 flex items-center justify-center text-[#ff6b6b] text-xs font-bold">
              {post.author_name ? post.author_name[0] : '?'}
            </div>
            <span className="text-white text-xs font-medium">{post.author_name}</span>
            <span className="text-[#a0a0ab] text-xs ml-auto">{timeAgo(post.created_at)}</span>
          </div>
          <p className="text-[#e8e8e8] text-sm leading-relaxed mb-4">{post.content}</p>
          <div className="flex items-center gap-4">
            <button onClick={handleUpvotePost}
              className={'flex items-center gap-1.5 text-sm transition-colors ' + (upvoted ? 'text-[#06d6a0]' : 'text-[#a0a0ab] hover:text-white')}
              data-testid="upvote-post-btn">
              <ChevronUp size={16} /> {post.upvote_count || 0}
            </button>
            <span className="flex items-center gap-1 text-xs text-[#a0a0ab]">
              <MessageSquare size={12} /> {post.reply_count || 0} reponses
            </span>
            <ShareButtons url={window.location.href} compact networks={['whatsapp']} />
            <button onClick={handleFlagPost}
              className="text-xs text-[#a0a0ab] hover:text-[#ff6b6b] ml-auto flex items-center gap-1">
              <Flag size={12} /> Signaler
            </button>
          </div>
        </div>

        <h2 className="text-white font-semibold text-sm mb-3">
          {replies.length} reponse{replies.length > 1 ? 's' : ''}
        </h2>
        <div className="space-y-3 mb-6">
          {replies.map(function(r) {
            return React.createElement(ReplyItem, {
              key: r.reply_id,
              reply: r,
              postAuthorId: post.author_id,
              postId: postId,
              onSolutionMark: handleSolutionMark,
              onRefresh: fetchPost,
              isNested: false
            });
          })}
          {replies.length === 0 && !post.is_closed && (
            <p className="text-center text-sm text-[#a0a0ab] py-4">Soyez le premier a repondre !</p>
          )}
        </div>

        {!post.is_closed ? (
          user ? (
            <form onSubmit={handleSubmitReply} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
              <p className="text-white text-sm font-medium">
                Votre reponse <span className="text-xs text-[#06d6a0]">+5 XP</span>
              </p>
              <textarea value={replyContent} onChange={function(e) { setReplyContent(e.target.value); }}
                placeholder="Partagez votre experience ou votre reponse..."
                rows={4} required minLength={5}
                className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[#ff6b6b]/50"
                data-testid="reply-textarea" />
              <button type="submit" disabled={submitting || replyContent.length < 5}
                className="btn-primary w-full py-2.5 text-sm disabled:opacity-50"
                data-testid="submit-reply-btn">
                {submitting ? 'Publication...' : 'Publier ma reponse'}
              </button>
            </form>
          ) : (
            <div className="text-center p-6 bg-white/5 border border-white/10 rounded-2xl">
              <p className="text-sm text-[#a0a0ab] mb-3">Connectez-vous pour repondre</p>
              <button onClick={function() { navigate('/auth'); }} className="btn-primary text-sm py-2 px-4">Se connecter</button>
            </div>
          )
        ) : (
          <div className="text-center p-4 bg-white/5 rounded-xl text-sm text-[#a0a0ab] flex items-center justify-center gap-2">
            <Lock size={14} /> Cette discussion est fermee
          </div>
        )}
      </div>
    </Layout>
  );
}
